import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { integrationsService } from '@/modules/settings/integrations/list/services/integrations.service';
import { AIService } from './ai.service';
import { toast } from 'sonner';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  data?: any;
}

interface AIContextType {
  isOpen: boolean;
  openChat: (initialMessage?: string) => void;
  closeChat: () => void;
  messages: Message[];
  sendMessage: (content: string, imageData?: string) => Promise<void>;
  clearMessages: () => void;
  isAnalyzing: boolean;
  activeAI: any;
  usedLimit: number;
  maxLimit: number;
  checkLimit: () => Promise<void>;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

export function AIProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const { profile } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeAI, setActiveAI] = useState<any>(null);
  const [usedLimit, setUsedLimit] = useState(0);
  const [maxLimit, setMaxLimit] = useState(20);

  const checkLimit = async () => {
    if (profile?.id && profile?.company_id) {
      const result = await AIService.checkUsageLimit(profile.id, profile.company_id);
      setUsedLimit(result.used);
      setMaxLimit(result.limit);
    }
  };

  useEffect(() => {
    checkLimit();
  }, [profile]);

  // Khôi phục lịch sử từ localStorage khi khởi tạo
  useEffect(() => {
    if (profile?.id) {
      const saved = localStorage.getItem(`landscape_ai_chat_${profile.id}`);
      if (saved) {
        try {
          setMessages(JSON.parse(saved));
        } catch (e) {
          console.error("Lỗi khôi phục lịch sử chat:", e);
        }
      }
    }
  }, [profile?.id]);

  // Lưu lịch sử vào localStorage mỗi khi có tin nhắn mới
  useEffect(() => {
    if (profile?.id && messages.length > 0) {
      localStorage.setItem(`landscape_ai_chat_${profile.id}`, JSON.stringify(messages));
    }
  }, [messages, profile?.id]);

  useEffect(() => {
    const checkAI = async () => {
      if (profile?.company_id) {
        const ai = await integrationsService.getLiveAI(profile.company_id);
        setActiveAI(ai);
      }
    };
    checkAI();
  }, [profile]);

  useEffect(() => {
    // Chỉ tạo lời chào nếu chưa có tin nhắn nào (lịch sử trống)
    if (profile && messages.length === 0) {
      const hour = new Date().getHours();
      const greetingTime = hour < 12 ? 'buổi sáng' : hour < 18 ? 'buổi chiều' : 'buổi tối';
      
      const gender = profile.gender?.toLowerCase();
      const honorific = gender === 'male' ? 'Anh' : gender === 'female' ? 'Chị' : 'Anh/Chị';
      const userName = profile.full_name || 'thành viên Landscape';
      
      const naturalGreetings = [
        `Chào ${greetingTime}, ${honorific} ${userName}! 🌿 ${honorific} cần em giúp gì hôm nay không ạ?`,
        `Chào ${greetingTime} tốt lành! ✨ Rất vui được gặp lại ${honorific} ${userName}.`,
        `Dạ chào ${honorific}! 🍃 Em là Trợ lý Landscape. Mình bắt đầu công việc hôm nay từ đâu ${honorific} nhỉ?`,
      ];

      const randomGreeting = naturalGreetings[Math.floor(Math.random() * naturalGreetings.length)];

      setMessages([
        { 
          role: 'assistant', 
          content: randomGreeting
        }
      ]);
    }
  }, [profile, activeAI]);

  const openChat = async (initialMessage?: any) => {
    setIsOpen(true);
    // Làm mới cấu hình AI khi mở chat để cập nhật Key/Model mới nhất
    if (profile?.company_id) {
      const ai = await integrationsService.getLiveAI(profile.company_id);
      setActiveAI(ai);
    }
    
    if (initialMessage && typeof initialMessage === 'string') {
      sendMessage(initialMessage);
    }
  };

  const closeChat = () => setIsOpen(false);

  const sendMessage = async (content: string, imageData?: string) => {
    if (usedLimit >= maxLimit) {
      toast.error('Sếp đã dùng hết lượt hỏi AI hôm nay rồi ạ.');
      return;
    }

    const userMsg: Message = { role: 'user', content, data: { image: imageData } };
    setMessages(prev => [...prev, userMsg]);
    setIsAnalyzing(true);

    try {
      if (activeAI) {
        // Gọi API thật qua service kèm profile và lịch sử để cá nhân hóa
        const { content: responseText, agent } = await AIService.chat(content, activeAI, profile, messages, imageData);
        const aiMsg: Message = { 
          role: 'assistant', 
          content: responseText,
          data: { agent } // Lưu thông tin chuyên gia vào data của message
        };
        setMessages(prev => [...prev, aiMsg]);
        
        if (profile?.id) {
          await AIService.incrementUsage(profile.id);
          setUsedLimit(prev => prev + 1);
        }
      } else {
        // Chế độ offline nếu chưa có key
        setTimeout(() => {
          const aiMsg: Message = { 
            role: 'assistant', 
            content: `Dạ, hiện tại sếp chưa cấu hình API Key cho AI Engine nào. Em chỉ có thể trả lời ở chế độ giới hạn. Sếp vui lòng vào Cài đặt > Tích hợp để kết nối bộ não AI nhé!` 
          };
          setMessages(prev => [...prev, aiMsg]);
        }, 1000);
      }
    } catch (error) {
      console.error('Lỗi AI:', error);
      toast.error('Có lỗi xảy ra khi kết nối với AI Engine.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
    if (profile?.id) {
      localStorage.removeItem(`landscape_ai_chat_${profile.id}`);
    }
  };

  return (
    <AIContext.Provider value={{ 
      isOpen, 
      openChat, 
      closeChat, 
      messages, 
      sendMessage, 
      clearMessages,
      isAnalyzing, 
      activeAI,
      usedLimit,
      maxLimit,
      checkLimit
    }}>
      {children}
    </AIContext.Provider>
  );
}

export function useAI() {
  const context = useContext(AIContext);
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
}
